import { z } from "zod";

import { config } from "@/config";

/**
 * What Shopify's installments banner needs for one variant: its id and the
 * plan data Shopify computes for it (eligible plans, price per term).
 */
export type ShopPayInstallmentsTerms = {
  variantId: string;
  meta: string;
};

const PAYMENT_TERMS_TAG =
  /<shopify-payment-terms[^>]*\bvariant-id="(\d+)"[^>]*\bshopify-meta="([^"]*)"/;

const decodeAttribute = (value: string): string =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

/**
 * Shopify only renders its installments banner from a Liquid theme, so the
 * plan data is read, at build time, from the store theme's own product page,
 * where Shopify prints it. Null when the page or the banner is missing, which
 * leaves the product page without the line rather than with a wrong amount.
 */
const FETCH_ATTEMPTS = 3;
const FETCH_TIMEOUT_MS = 10_000;

/** The build runs once per deploy, so a dropped connection is retried rather
 *  than shipping the page without its installments line until the next one. */
const fetchWithRetry = async (url: string): Promise<Response> => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fetch(url, {
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
    } catch (error) {
      if (attempt === FETCH_ATTEMPTS) throw error;
      console.error(
        `Failed to fetch ${url} (attempt ${attempt} of ${FETCH_ATTEMPTS}), retrying:`,
        error,
      );
    }
  }
};

export const getShopPayInstallmentsTerms = async (
  productHandle: string,
): Promise<ShopPayInstallmentsTerms | null> => {
  const url = `https://${config.shopify.installmentsStoreDomain}/products/${productHandle}`;
  try {
    const response = await fetchWithRetry(url);
    if (!response.ok) {
      console.error(
        `Failed to load Shop Pay Installments terms: ${url} returned ${response.status}`,
      );
      return null;
    }
    const match = PAYMENT_TERMS_TAG.exec(await response.text());
    if (match === null || match[1] === undefined || match[2] === undefined) {
      console.error(
        `Failed to load Shop Pay Installments terms: no banner on ${url}`,
      );
      return null;
    }
    const meta = decodeAttribute(match[2]);
    const parsed: unknown = JSON.parse(meta);
    const eligible =
      typeof parsed === "object" &&
      parsed !== null &&
      Array.isArray((parsed as { variants?: unknown }).variants) &&
      (parsed as { variants: { eligible?: unknown }[] }).variants.some(
        (variant) => variant.eligible === true,
      );
    if (!eligible) {
      console.error(
        `Failed to load Shop Pay Installments terms: ${productHandle} is not eligible`,
      );
      return null;
    }
    return { variantId: match[1], meta };
  } catch (error) {
    console.error("Failed to load Shop Pay Installments terms:", error);
    return null;
  }
};

/*
 * Shopify's plan data, as its theme prints it. Only the fields read here are
 * declared; the rest passes through, since termsForAmount writes the data
 * back for Shopify's own element.
 */
const FinancingPlanSchema = z.object({
  min_price_amount: z.number(),
  max_price_amount: z.number(),
  terms: z.array(
    z.object({
      apr: z.number(),
      loan_type: z.string(),
      installments_count: z.number(),
    }),
  ),
});

const PlanDataSchema = z.looseObject({
  financing_plans: z.array(z.unknown()).optional(),
  variants: z.array(
    z.looseObject({
      id: z.union([z.string(), z.number()]),
      number_of_payment_terms: z.number().optional(),
    }),
  ),
});

type PlanData = z.infer<typeof PlanDataSchema>;

/** The plan data in `terms`, or null (logged) when it can't be read. */
const parsePlanData = (terms: ShopPayInstallmentsTerms): PlanData | null => {
  try {
    const parsed = PlanDataSchema.safeParse(JSON.parse(terms.meta));
    if (parsed.success) return parsed.data;
    console.error("Failed to read Shop Pay Installments plans:", parsed.error);
    return null;
  } catch (error) {
    console.error("Failed to read Shop Pay Installments plans:", error);
    return null;
  }
};

/**
 * The monthly terms Shopify offers at `amount`: those of every plan whose
 * price band covers it, interest-bearing ones included. Split pay is left
 * out: it falls due every two weeks, not monthly. Null when the data is
 * unreadable; a malformed plan alone is skipped.
 */
const monthlyTermsAt = (terms: ShopPayInstallmentsTerms, amount: number) => {
  const data = parsePlanData(terms);
  if (data === null) return null;
  return (data.financing_plans ?? [])
    .flatMap((value) => {
      const plan = FinancingPlanSchema.safeParse(value);
      return plan.success &&
        plan.data.min_price_amount <= amount &&
        amount <= plan.data.max_price_amount
        ? plan.data.terms
        : [];
    })
    .filter((term) => term.loan_type === "interest");
};

/**
 * The longest 0% APR monthly plan Shopify offers at `amount`, in payments,
 * read from the same plan data the installments line renders. Null when the
 * data is unreadable or no 0% plan covers the amount.
 */
export const zeroAprInstallmentCount = (
  terms: ShopPayInstallmentsTerms,
  amount: number,
): number | null => {
  const counts = (monthlyTermsAt(terms, amount) ?? [])
    .filter((term) => term.apr === 0)
    .map((term) => term.installments_count);
  return counts.length === 0 ? null : Math.max(...counts);
};

/** One monthly payment on `amount` over `months` at `apr` percent a year. */
const paymentPerMonth = (amount: number, months: number, apr: number) => {
  if (apr === 0) return amount / months;
  const rate = apr / 100 / 12;
  return (amount * rate) / (1 - (1 + rate) ** -months);
};

const formatCurrency = (
  value: number,
  currencyCode: string,
  options: Intl.NumberFormatOptions = {},
) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    ...options,
  }).format(value);

/**
 * The lowest monthly payment Shopify offers on `amount`, as its own
 * installments line quotes it ("From $X/mo"): every monthly term at the
 * amount, amortised. Without plan data (a store with no Shop Pay
 * Installments) `fallbackMonths` at 0% stands in; null when the data is
 * unreadable or no plan covers the amount.
 */
export const lowestMonthlyPayment = (
  amount: number | null,
  currencyCode: string,
  installments: ShopPayInstallmentsTerms | null,
  fallbackMonths: number,
): string | null => {
  if (amount === null || amount <= 0) return null;
  let perMonth: number | null = amount / fallbackMonths;
  if (installments !== null) {
    const monthlyTerms = monthlyTermsAt(installments, amount);
    if (monthlyTerms === null) return null;
    const payments = monthlyTerms.map((term) =>
      paymentPerMonth(amount, term.installments_count, term.apr),
    );
    perMonth = payments.length === 0 ? null : Math.min(...payments);
  }
  if (perMonth === null) return null;
  // The nearest cent, as Shopify's own line quotes it.
  const cents = Math.round(perMonth * 100) / 100;
  return formatCurrency(cents, currencyCode, {
    minimumFractionDigits: Number.isInteger(cents) ? 0 : 2,
  });
};

/**
 * Shopify's terms re-quoted for the cart total instead of the one Matic it
 * was fetched for, its lending plans kept intact, so the installments pop-up
 * matches the bar. Null when the amount or the data can't be read.
 */
export const termsForAmount = (
  terms: ShopPayInstallmentsTerms,
  amount: number,
  currencyCode: string,
): ShopPayInstallmentsTerms | null => {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const data = parsePlanData(terms);
  if (data === null) return null;
  return {
    ...terms,
    meta: JSON.stringify({
      ...data,
      variants: data.variants.map((variant) => {
        const count = variant.number_of_payment_terms;
        if (
          String(variant.id) !== terms.variantId ||
          count === undefined ||
          count <= 0
        )
          return variant;
        const perTerm = Math.round((amount / count) * 100) / 100;
        return {
          ...variant,
          full_price: formatCurrency(amount, currencyCode),
          full_price_amount: amount,
          price_per_term: formatCurrency(perTerm, currencyCode),
          price_per_term_amount: perTerm,
        };
      }),
    }),
  };
};
